import { resolveContact } from "../../../src/shop-contact";
import { Go, type GoFn } from "./route";
import { ChannelButton, SocialLinks, SubscribeForm } from "./Subscribe";
import type { ShopConfig } from "./types";
import { CONSENT_OPEN_EVENT, hasAdsIds } from "./ads";
import { useEffect, useState } from "react";
import { get } from "./shared";
import { APP_LINKS, StoreBadges } from "./AppPromo";

const EXT = { target: "_blank", rel: "noopener noreferrer" } as const;
/**
 * The owner's Google Business Profile. A plain link, deliberately with NO rating number: Google's
 * Places terms forbid storing the rating, and a typed-in number goes stale (shop-reviews.ts).
 */
export const GOOGLE_REVIEWS_URL = "https://share.google/yIRqrecbzN3zTdQYC";

/** The store policies (`/policies/<handle>`); verify:shop-nav checks this list against SHOP_POLICIES. */
export const POLICY_LINKS: ReadonlyArray<readonly [string, string]> = [
  ["refund-policy", "Refund policy"],
  ["shipping-policy", "Shipping policy"],
  ["privacy-policy", "Privacy policy"],
  ["terms-of-service", "Terms of service"],
  ["contact-information", "Contact information"],
];

export function Footer({ go, config }: { go: GoFn; config: ShopConfig | null }) {
  const { collect } = resolveContact(config);
  /** The "Buying guides" link shows only while at least one guide is published (drafts are invisible). */
  const [hasGuides, setHasGuides] = useState(false);
  useEffect(() => {
    const ac = new AbortController();
    get<{ guides: unknown[] }>("/shop/guides", ac.signal).then((d) => setHasGuides(d.guides.length > 0)).catch(() => undefined);
    return () => ac.abort();
  }, []);
  return (
    <footer className="foot">
      <div className="wrap foot-cols">
        <div>
          <h2>Z S MERCH</h2>
          <p style={{ maxWidth: "30ch" }}>Tools, home &amp; garden, electronics and everyday essentials for retail and wholesale customers since 2023.</p>
          <SocialLinks className="social-foot" handles />
        </div>
        <div>
          <h2>Visit us</h2>
          <ul>
            <li>{collect.name}</li>
            <li>{collect.address}</li>
            <li><a href={collect.mapUrl} {...EXT}>Get directions<span className="sr-only"> (opens in a new tab)</span></a></li>
            <li><a href={GOOGLE_REVIEWS_URL} {...EXT}>See our reviews on Google ★<span className="sr-only"> (opens in a new tab)</span></a></li>
          </ul>
        </div>
        <div>
          <h2>Help</h2>
          <ul>
            <li><Go to={{ name: "faq" }} go={go}>FAQ: delivery, returns and payment</Go></li>
            <li><Go to={{ name: "track" }} go={go}>Track an order</Go></li>
            <li><Go to={{ name: "contact" }} go={go}>Contact us</Go></li>
            <li><Go to={{ name: "about" }} go={go}>About us</Go></li>
            <li><Go to={{ name: "collections" }} go={go}>All collections</Go></li>
            {hasGuides && <li><Go to={{ name: "guides" }} go={go}>Buying guides</Go></li>}
            <li><Go to={{ name: "account", tab: "up" }} go={go}>Wholesale account</Go></li>
          </ul>
        </div>
        {(APP_LINKS.android || APP_LINKS.ios) && (
          <div>
            <h2>Get the app</h2>
            <p>Shop Z S MERCH from your {APP_LINKS.ios ? "phone" : "Android phone"}.</p>
            <StoreBadges where="footer" />
          </div>
        )}
        <div>
          <h2>Payment</h2>
          <ul><li>Visa &amp; Mastercard</li><li>Instant EFT</li><li>Wholesale accounts on terms</li></ul>
        </div>
        <div>
          <h2>Policies</h2>
          {/* Handles + titles of src/shop-policies.ts, typed here so the footer does not pull ~40 KB of policy text into the main chunk. */}
          <ul>
            {POLICY_LINKS.map(([handle, title]) => <li key={handle}><Go to={{ name: "policy", handle }} go={go}>{title}</Go></li>)}
            {/* Reopens the ads cookie banner (ConsentBanner.tsx). Only while ads tracking is configured. */}
            {hasAdsIds(config?.ads) && <li><button type="button" className="foot-linkbtn" onClick={() => window.dispatchEvent(new Event(CONSENT_OPEN_EVENT))}>Cookie settings</button></li>}
          </ul>
        </div>
      </div>
      <div className="wrap foot-sub">
        <div>
          <h2>Get the Deal of the Day and new stock first</h2>
          <p>Ticking a box is how you say yes. Tick email, WhatsApp, or both.</p>
          <ChannelButton />
        </div>
        <SubscribeForm source="shop-footer" compact />
      </div>
    </footer>
  );
}
